import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/query/query-keys';
import type { TripDetail, TripDetailCity, TripCityFuel } from '@/hooks/trips/types';

interface FuelAmountUpdate {
  cityId: number;
  fuelId: number;
  amount: number;
}

interface UpdateFuelAmountsInput {
  tripId: string;
  updates: FuelAmountUpdate[];
}

interface MutationContext {
  previousTrip?: TripDetail;
  previousCityList?: TripDetailCity[];
  previousCityDetailMap: Map<number, TripDetailCity | undefined>;
}

interface ApiResponseEntry {
  id: number;
  amount: number;
}

function normalizeResponse(payload: unknown): ApiResponseEntry[] | null {
  if (!Array.isArray(payload)) {
    return null;
  }

  const entries: ApiResponseEntry[] = [];
  for (const item of payload) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const record = item as Record<string, unknown>;
    const id = Number(record.id);
    const amount = Number(record.amount);
    if (!Number.isFinite(id) || !Number.isFinite(amount)) {
      continue;
    }
    entries.push({ id, amount });
  }

  return entries;
}

export function useUpdateFuelAmounts() {
  const queryClient = useQueryClient();

  return useMutation<ApiResponseEntry[] | null, Error, UpdateFuelAmountsInput, MutationContext>({
    mutationFn: async ({ updates }) => {
      const payload = updates.map((update) => ({
        id: update.fuelId,
        amount: update.amount,
      }));

      const response = await apiFetch('/api/v1/business-trip-fuels/amounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const message =
          data && typeof data === 'object' && 'error' in (data as Record<string, unknown>)
            ? String(
                (data as Record<string, { message?: string }>).error?.message ??
                  'Failed to update fuel amount'
              )
            : 'Failed to update fuel amount';
        throw new Error(message);
      }

      return normalizeResponse(data);
    },
    onMutate: async ({ tripId, updates }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.trip(tripId) });
      await queryClient.cancelQueries({ queryKey: queryKeys.tripCities(tripId) });

      const tripKey = queryKeys.trip(tripId);
      const cityListKey = queryKeys.tripCities(tripId);

      const previousTrip = queryClient.getQueryData<TripDetail>(tripKey);
      const previousCityList = queryClient.getQueryData<TripDetailCity[]>(cityListKey);
      const previousCityDetailMap = new Map<number, TripDetailCity | undefined>();

      updates.forEach((update) => {
        const cityDetailKey = queryKeys.tripCity(tripId, update.cityId);
        if (!previousCityDetailMap.has(update.cityId)) {
          previousCityDetailMap.set(
            update.cityId,
            queryClient.getQueryData<TripDetailCity>(cityDetailKey)
          );
        }
      });

      const applyUpdate = (fuel: TripCityFuel, update: FuelAmountUpdate) =>
        fuel.id === update.fuelId ? { ...fuel, fuelAmount: update.amount } : fuel;

      if (previousTrip) {
        queryClient.setQueryData<TripDetail>(tripKey, {
          ...previousTrip,
          cities: previousTrip.cities?.map((city) => {
            const cityUpdates = updates.filter((update) => update.cityId === city.id);
            if (cityUpdates.length === 0) {
              return city;
            }
            return {
              ...city,
              fuels: city.fuels.map((fuel) => cityUpdates.reduce(applyUpdate, fuel)),
            };
          }),
        });
      }

      if (previousCityList) {
        queryClient.setQueryData<TripDetailCity[]>(cityListKey, (current) =>
          current?.map((city) => {
            const cityUpdates = updates.filter((update) => update.cityId === city.id);
            if (cityUpdates.length === 0) {
              return city;
            }
            return {
              ...city,
              fuels: city.fuels.map((fuel) => cityUpdates.reduce(applyUpdate, fuel)),
            };
          })
        );
      }

      updates.forEach((update) => {
        const cityDetailKey = queryKeys.tripCity(tripId, update.cityId);
        queryClient.setQueryData<TripDetailCity>(cityDetailKey, (current) => {
          if (!current) {
            return current;
          }
          return {
            ...current,
            fuels: current.fuels.map((fuel) => applyUpdate(fuel, update)),
          };
        });
      });

      return { previousTrip, previousCityList, previousCityDetailMap };
    },
    onError: (_error, { tripId, updates }, context) => {
      if (!context) {
        return;
      }

      const tripKey = queryKeys.trip(tripId);
      const cityListKey = queryKeys.tripCities(tripId);

      if (context.previousTrip) {
        queryClient.setQueryData(tripKey, context.previousTrip);
      }

      if (context.previousCityList) {
        queryClient.setQueryData(cityListKey, context.previousCityList);
      }

      updates.forEach((update) => {
        const cityDetailKey = queryKeys.tripCity(tripId, update.cityId);
        const previousCityDetail = context.previousCityDetailMap.get(update.cityId);
        if (previousCityDetail !== undefined) {
          queryClient.setQueryData(cityDetailKey, previousCityDetail);
        }
      });
    },
    onSuccess: (response, { tripId, updates }) => {
      const tripKey = queryKeys.trip(tripId);
      const cityListKey = queryKeys.tripCities(tripId);

      const responseAmountMap = new Map<number, number>();
      if (response) {
        response.forEach((entry) => {
          responseAmountMap.set(entry.id, entry.amount);
        });
      }

      const applyFinalAmount = (fuel: TripCityFuel): TripCityFuel => {
        const override = responseAmountMap.get(fuel.id);
        if (override != null) {
          return { ...fuel, fuelAmount: override };
        }

        const update = updates.find((item) => item.fuelId === fuel.id);
        return update ? { ...fuel, fuelAmount: update.amount } : fuel;
      };

      queryClient.setQueryData<TripDetail>(tripKey, (current) =>
        current
          ? {
              ...current,
              cities: current.cities?.map((city) => ({
                ...city,
                fuels: city.fuels.map(applyFinalAmount),
              })),
            }
          : current
      );

      queryClient.setQueryData<TripDetailCity[]>(cityListKey, (current) =>
        current?.map((city) => ({
          ...city,
          fuels: city.fuels.map(applyFinalAmount),
        }))
      );

      updates.forEach((update) => {
        const cityDetailKey = queryKeys.tripCity(tripId, update.cityId);
        queryClient.setQueryData<TripDetailCity>(cityDetailKey, (current) =>
          current
            ? {
                ...current,
                fuels: current.fuels.map(applyFinalAmount),
              }
            : current
        );
      });
    },
    onSettled: (_result, _error, { tripId, updates }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trip(tripId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tripCities(tripId) });
      updates.forEach((update) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.tripCity(tripId, update.cityId) });
      });
    },
  });
}
